"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import bigDecimal from "js-big-decimal";
import { cn } from "utils";
import dayjs from "dayjs";
import { useMemoizedFn } from "ahooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  TransactionFormData,
  TransactionModalProps,
  TransactionType,
} from "../types";
import { Coin } from "../Coin";
import { DateSelect } from "../DateSelect/DateSelect";
import { NumericInput } from "../NumericInput";
import { Stock, Token } from "@/src/features/api/openData";
import {
  calculateTotal,
  formatBigDecimalString,
  safeParseNumber,
} from "@/src/utils/decimalMath";
import {
  useCreatePositionOrder,
  useUpdatePositionOrder,
} from "@/src/hooks/usePositionOrder";

import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
import { I18nUtil } from "@utils/i18n";
import { PositionAssetTypeEnum } from "@/src/features/api/memberApi";
import { QUERY_KEYS } from "@/src/config";
import Translations from "@translate/components/Portfolio/Transactions/TransactionModal/TransactionModal";
import { sleep } from "@/src/utils/sleep";
import { usePortfolioPage } from "@/src/hooks/usePortfolioPage";
import { getCurrencySymbol } from "@utils/stock";

const I18n = I18nUtil.createScoped(Translations);

const defaultForm: TransactionFormData = {
  type: TransactionType.Buy,
  quantity: "",
  pricePerCoin: "",
  dateTime: "",
  fees: "",
  asset: {
    assetId: "",
    assetType: null,
    currency: "USD",
  },
};

const parseNumber = (value: string): number => {
  return safeParseNumber(value);
};

interface TransactionModalContentProps {
  mode: "add" | "edit";
  initialData: TransactionModalProps["initialData"];
  onConfirm: () => void;
  onCancel: () => void;
  assetIds?: string[];
  disabledSelect?: boolean;
}

const TransactionModalContent: React.FC<TransactionModalContentProps> = ({
  mode,
  initialData,
  onConfirm,
  onCancel,
  disabledSelect = false,
}) => {
  const [form, setForm] = useState<TransactionFormData>(defaultForm);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  // 仅允许选择「当前时间往前 10 天（含）之后」的日期时间
  const minDate = dayjs().subtract(30, "day").startOf("day").toDate();
  const { refetch, data } = usePortfolioPage();

  const transformedAssetData = data?.map((item) => {
    const isToken = item.asset_type === PositionAssetTypeEnum.TOKEN;
    const rawPrice = isToken
      ? (item.asset as Token).market_data?.current_price || 0
      : (item.asset as Stock)?.trading_data?.current_price || 0;

    // Truncate price to maximum 8 decimal places
    const truncatedPrice = Math.trunc(rawPrice * 1e8) / 1e8;

    return {
      assetId: item?.asset?.id || "",
      assetType: item.asset_type as PositionAssetTypeEnum,
      coin: item.asset?.short_name
        ? `${item.asset?.short_name}.HK`
        : item.asset?.symbol || "",
      coinIcon: item.asset?.logo || "",
      price: truncatedPrice,
      currency: item.asset?.currency || "USD",
    };
  });

  const initPricePerCoin = useMemo(() => {
    if (transformedAssetData && transformedAssetData.length > 0) {
      return (
        transformedAssetData
          .find((item) => item.assetId === initialData?.assetId)
          ?.price.toString() || ""
      );
    }
    return "";
  }, [transformedAssetData, initialData?.assetId]);
  useEffect(() => {
    if (initPricePerCoin) {
      setForm((prev: TransactionFormData) => {
        return {
          ...prev,
          pricePerCoin:
            initialData.pricePerCoin !== undefined
              ? initialData.pricePerCoin.toString()
              : initPricePerCoin,
        };
      });
    }
  }, [initPricePerCoin, initialData.pricePerCoin]);

  // API hooks
  const createOrderMutation = useCreatePositionOrder({
    onSuccess: async () => {
      await sleep(1000);
      onConfirm();
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POSITION_ORDERS] });
    },
  });

  const updateOrderMutation = useUpdatePositionOrder({
    onSuccess: async () => {
      await sleep(1000);
      setLoading(false);
      onConfirm();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POSITION_ORDERS] });
    },
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      type: initialData.type || TransactionType.Buy,
      asset: initialData.assetId
        ? {
            assetId: initialData.assetId,
            assetType: initialData.assetType || null,
            currency: initialData.currency || "USD",
          }
        : undefined,
      quantity: initialData.quantity?.toString() || "",
      dateTime: initialData.dateTime || dayjs().toISOString(),
      fees: initialData.fees?.toString() || "",
    }));
  }, [initialData]);

  const handleChange = useCallback(
    (
      field: keyof TransactionFormData,
      value: string | { assetId: string; assetType: PositionAssetTypeEnum },
    ) => {
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleTypeChange = useCallback((type: TransactionType) => {
    setForm((prev) => ({
      ...prev,
      type,
    }));
  }, []);

  const totalReceived = useMemo(() => {
    const quantity = form.quantity;
    const price = form.pricePerCoin;
    const fees = form.fees;
    const type = form.type === TransactionType.Buy ? "BUY" : "SELL";

    // Calculate total using BigDecimal:
    // BUY: price * quantity + fees (total cost)
    // SELL: price * quantity - fees (net income)
    const total = calculateTotal(quantity, price, fees, type);

    return total;
  }, [form.quantity, form.pricePerCoin, form.fees, form.type]);

  const isValid = useMemo(() => {
    const quantity = parseNumber(form.quantity);

    const dateTime = form.dateTime.trim().length > 0;

    return quantity > 0 && dateTime && form?.asset?.assetId;
  }, [form.dateTime, form.quantity, form?.asset?.assetId]);
  // 计算是否正在加载
  const isLoading =
    createOrderMutation.isPending || updateOrderMutation.isPending || loading;

  const handleSubmit = useMemoizedFn(async () => {
    setLoading(true);
    if (!isValid) {
      return;
    }

    const timestamp = new Date(form.dateTime).getTime();

    // 直接映射字段为API请求格式
    const orderRequest = {
      side:
        form.type === TransactionType.Buy
          ? ("BUY" as const)
          : ("SELL" as const),
      asset_id: form.asset?.assetId || "",
      asset_type: form?.asset?.assetType as PositionAssetTypeEnum,
      amount: Number(form.quantity),
      fee: Number(form.fees),
      price: Number(form.pricePerCoin),
      order_time: timestamp,
      platform_order_id:
        initialData.platform_order_id ||
        dayjs(timestamp).format("YYYY-MM-DD-HH-mm-ss") +
          "-" +
          Math.floor(Math.random() * 1000) +
          1,
    };

    try {
      if (mode === "add") {
        await createOrderMutation.mutateAsync({
          accountId: initialData.accountId,
          data: orderRequest,
        });
      } else {
        await updateOrderMutation.mutateAsync({
          accountId: initialData.accountId,
          orderId: initialData.orderId || "",
          data: orderRequest,
        });
      }
      await refetch?.();
    } catch (error) {
      setLoading(false);
    }
  });

  const handleCoinChange = useCallback(
    (value: {
      assetId: string;
      assetType: PositionAssetTypeEnum;
      currency: string;
    }) => {
      handleChange("asset", value);
      const asset = transformedAssetData?.find(
        (item) => item.assetId === value.assetId,
      );
      if (asset) {
        handleChange("pricePerCoin", asset.price.toString());
      }
    },
    [handleChange, transformedAssetData],
  );

  const title =
    mode === "add" ? I18n.t("Add Transaction") : I18n.t("Edit Transaction");
  const submitLabel =
    mode === "add" ? I18n.t("Add Transaction") : I18n.t("Confirm");
  return (
    <div className="relative flex w-full flex-col gap-6">
      <div className="flex items-start justify-between sticky top-0 bg-background_solid_secondary pt-6">
        <div>
          <h2
            aria-label="Transaction Modal h2"
            data-openreplay-label="Transaction Modal h2 Semantic"
            id="sem-transaction-modal-h2"
            className="headline_regular_s text-content_primary"
          >
            {title}
          </h2>
        </div>
        <button
          data-openreplay-label="Transaction Modal Button"
          id="btn-transaction-modal"
          type="button"
          aria-label={I18n.t("Cancel")}
          className="flex size-7 items-center justify-center rounded-full text-content_primary transition hover:text-content_secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-content_primary/50"
          onClick={onCancel}
        >
          <Icon name="close" size={24} />
        </button>
      </div>
      <div>
        <div className="flex items-center gap-1 rounded-full bg-background_white_overlay10 p-0.5">
          {([TransactionType.Buy, TransactionType.Sell] as const).map(
            (type) => {
              const isActive = form.type === type;
              return (
                <button
                  aria-label="Transaction Modal"
                  data-openreplay-label="Transaction Modal Button"
                  id="btn-transaction-modal-2"
                  key={type}
                  type="button"
                  onClick={() => handleTypeChange(type)}
                  data-actived={isActive}
                  className={cn(
                    "flex flex-1 text-content_primary items-center justify-center rounded-full px-3 py-1 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-background_white_overlay70 actived:bg-background_solid_neutral_primary",
                  )}
                >
                  <span className="body_medium_m">
                    {type === TransactionType.Buy
                      ? I18n.t("Buy")
                      : I18n.t("Sell")}
                  </span>
                </button>
              );
            },
          )}
        </div>
      </div>
      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Coin
          value={{
            assetId: form.asset?.assetId || "",
            assetType: form.asset?.assetType as PositionAssetTypeEnum,
            currency: form.asset?.currency || "USD",
          }}
          onChange={handleCoinChange}
          data={transformedAssetData}
          label={I18n.t("Coin")}
          disabled={disabledSelect}
        />

        <div className="flex gap-4">
          <div className="flex w-full flex-col gap-2">
            <label
              className="body_medium_s text-content_secondary"
              htmlFor="transaction-quantity"
            >
              {I18n.t("Quantity")}
            </label>
            <NumericInput
              value={
                form.quantity ? new bigDecimal(form.quantity).getValue() : ""
              }
              onChange={(value) => handleChange("quantity", value)}
              placeholder="0.00"
              precision={8}
              maxIntegerDigits={10}
            />
          </div>

          <div className="flex w-full flex-col gap-2">
            <label
              className="body_medium_s text-content_secondary"
              htmlFor="transaction-price"
            >
              {I18n.t("Price Per Symbol")}
            </label>
            <NumericInput
              value={
                form.pricePerCoin
                  ? new bigDecimal(form.pricePerCoin).getValue()
                  : ""
              }
              onChange={(value) => handleChange("pricePerCoin", value)}
              placeholder="0.00"
              precision={8}
              prefix={getCurrencySymbol(
                form.asset?.currency as Stock["currency"],
              )}
              maxIntegerDigits={10}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label
            className="body_medium_s text-content_secondary"
            htmlFor="transaction-datetime"
          >
            {I18n.t("Date & Time")}
          </label>
          <DateSelect
            value={form.dateTime ? dayjs(form.dateTime).toDate() : undefined}
            onChange={(date) =>
              handleChange("dateTime", date ? date.toISOString() : "")
            }
            className="h-9 w-full rounded-md border-border_white_transparent5 bg-background_white_overlay5"
            disabled={mode !== "add"}
            disabledTime={[{ before: minDate, after: dayjs().toDate() }]}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            className="body_medium_s text-content_secondary"
            htmlFor="transaction-fees"
          >
            {I18n.t("Fees")}
          </label>
          <NumericInput
            value={form.fees ? new bigDecimal(form.fees).getValue() : ""}
            onChange={(value) => handleChange("fees", value)}
            placeholder="0.00"
            precision={8}
            prefix={getCurrencySymbol(
              form.asset?.currency as Stock["currency"],
            )}
            maxIntegerDigits={10}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Total")}
          </span>
          <div className="flex items-center gap-2 h-8">
            {mode === "edit" ? (
              <Icon name="dollar" size={20} className="text-content_primary" />
            ) : null}
            <span
              className={cn(
                totalReceived.length > 18 ? "title_l" : "headline_regular_m",
                "text-content_primary",
              )}
              aria-live="polite"
            >
              {`${getCurrencySymbol(form.asset?.currency as Stock["currency"])} ${formatBigDecimalString(totalReceived)}`}
            </span>
          </div>
        </div>

        <button
          aria-label="Transaction Modal"
          data-openreplay-label="Transaction Modal Button"
          id="btn-transaction-modal-3"
          type="submit"
          className={cn(
            "mt-2 h-11 rounded-md body_bold_m transition focus:outline-none focus-visible:ring-2 focus-visible:ring-background_white_overlay70",
            "button primary_button button_m group",
          )}
          disabled={!isValid || isLoading}
          data-loading={isLoading}
        >
          <span className="group-loading:hidden text-content_inverse_primary">
            {submitLabel}
          </span>
          {isLoading && (
            <Icon
              name="ic_loading2"
              size={20}
              color={ospThemes.colors.content_inverse_primary}
              className={cn(
                "hidden animate-spin group-loading:block flex justify-items-center",
              )}
            />
          )}
        </button>
      </form>
    </div>
  );
};

export const TransactionCreateAble = async (
  props: TransactionModalProps,
): Promise<void> => {
  return new Promise((resolve) => {
    let instanceRef: Promise<boolean> | null = null;

    const handleCancel = () => {
      if (instanceRef) {
        EdgenModalCreateAble.end(instanceRef, false);
      }
      resolve();
    };

    const handleConfirm = () => {
      if (instanceRef) {
        EdgenModalCreateAble.end(instanceRef, true);
      }
      resolve();
    };

    instanceRef = EdgenModalCreateAble.call({
      title: null,
      styles: {
        content: {
          width: 400,
          background: "transparent",
          padding: 0,
        },
        body: {
          padding: 0,
        },
      },
      containerClassName: "px-6 pb-6 rounded-2xl bg-background_solid_secondary",
      children: (
        <TransactionModalContent
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          {...props}
        />
      ),
    });
  });
};
